// src/components/lead/sections/LeadOwnerTransferPanel.tsx
import { useCallback, useEffect, useState } from "react";
import type { Lead } from "@/types/lead";
import {
  fetchLeadTransferCandidates,
  reassignLeadToOrgMember,
  type LeadTransferCandidate,
} from "@/lib/lead/transferLeadOwner";

function displayForMember(m: LeadTransferCandidate): string {
  const n = (m.full_name || m.display_name || "").trim();
  if (n) return `${n}${m.email ? ` · ${m.email}` : ""}`;
  return m.email || m.user_id;
}

export function LeadOwnerTransferPanel(props: {
  organizationId: string;
  currentUserId: string;
  lead: Lead;
  disabled?: boolean;
  onTransferredAway: (leadId: string) => void;
  setToast: (t: { message: string; type: "success" | "error" } | null) => void;
}) {
  const { organizationId, currentUserId, lead, disabled, onTransferredAway, setToast } = props;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState<LeadTransferCandidate[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");

  const load = useCallback(async () => {
    setLoadErr(null);
    const { data, error } = await fetchLeadTransferCandidates(organizationId);
    if (error) {
      setLoadErr(error);
      setMembers([]);
      return;
    }
    const others = (data ?? []).filter((m) => m.user_id && m.user_id !== currentUserId);
    setMembers(others);
  }, [organizationId, currentUserId]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const onSubmit = async () => {
    const target = selectedUserId.trim();
    if (!target) {
      setToast({ message: "Choose a teammate to assign this lead.", type: "error" });
      return;
    }
    setBusy(true);
    try {
      const res = await reassignLeadToOrgMember({ leadId: lead.id, newOwnerUserId: target });
      if (!res.ok) {
        setToast({
          message: res.error.includes("permission")
            ? "You do not have permission to reassign this lead (owner or org admin only)."
            : res.error,
          type: "error",
        });
        return;
      }
      setToast({ message: "Lead reassigned.", type: "success" });
      setOpen(false);
      onTransferredAway(lead.id);
    } finally {
      setBusy(false);
    }
  };

  if (lead.sent || lead.isTest) return null;

  if (!open) {
    return (
      <div className="mt-3 text-sm">
        <button
          type="button"
          disabled={!!disabled}
          className="text-indigo-600 dark:text-indigo-400 underline underline-offset-2 decoration-indigo-400/80 hover:text-indigo-800 dark:hover:text-indigo-200 disabled:opacity-50"
          onClick={() => setOpen(true)}
        >
          Transfer this lead to someone else…
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-dashed border-slate-200 dark:border-slate-600 bg-slate-50/60 dark:bg-slate-800/30 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-slate-700 dark:text-slate-200">Transfer this lead</div>
          <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
            Hand this lead off to someone else in your organization.
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs text-slate-600 dark:text-slate-400 underline underline-offset-2"
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>

      <div className="mt-3 space-y-2 border-t border-slate-200 dark:border-slate-600 pt-3">
        {loadErr ? (
          <p className="text-xs text-red-700 dark:text-red-300">{loadErr}</p>
        ) : null}
        {members && members.length === 0 && !loadErr ? (
          <p className="text-xs text-slate-600 dark:text-slate-400">No other active members in this org.</p>
        ) : null}
        {members && members.length > 0 ? (
          <>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Teammate</label>
            <select
              className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm px-3 py-2"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">— Select —</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {displayForMember(m)}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || !selectedUserId}
              className="w-full py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              onClick={() => void onSubmit()}
            >
              {busy ? "Assigning…" : "Assign lead"}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
